import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSmartNavigate } from '@/lib/nav-prefs/use-smart-navigate';
import { useTranslation } from 'react-i18next';
import { useClient } from '@/hooks/use-clients';
import { useSalesDocuments } from '@/hooks/use-sales';
import { usePayments } from '@/hooks/use-payments';
import { useCompanySettings } from '@/hooks/use-settings';
import {
  useClientRepresentatives,
  useRepresentatives,
  useLinkRepresentativeToClient,
  useUnlinkRepresentativeFromClient,
} from '@/hooks/use-representatives';
import { Card, CardContent } from '@erp/ui';
import { Badge } from '@erp/ui';
import { Button } from '@erp/ui';
import { FilterBar, FilterField } from '@erp/ui';
import { Separator } from '@erp/ui';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@erp/ui';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@erp/ui';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  Input,
} from '@erp/ui';
import { ArrowRight, Pencil, Plus, Trash2, Search } from '@/components/icons';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { ClientFormDialog } from './ClientFormDialog';
import { RepresentativeFormDialog } from '@/pages/representatives/RepresentativeFormDialog';
import type { Representative } from '@erp/data-access/interfaces';

function formatPrice(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  return n.toLocaleString('fr-TN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}
function formatDate(d: string): string {
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
}

const DOC_TYPE_LABELS: Record<string, string> = {
  quote: 'sales.quote', order: 'sales.order', delivery: 'sales.delivery',
  invoice: 'sales.invoice', credit_note: 'sales.creditNote',
};
const DOC_PATHS: Record<string, string> = {
  quote: '/sales/quotes', order: '/sales/orders', delivery: '/sales/deliveries',
  invoice: '/sales/invoices', credit_note: '/sales/credit-notes',
};
const DOC_TYPES_ORDER = ['quote', 'order', 'delivery', 'invoice', 'credit_note'] as const;

export function ClientProfilePage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useSmartNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const [docFilter, setDocFilter] = useState<string>('all');
  const { data: client, isLoading } = useClient(id ?? '');
  const { data: salesDocs } = useSalesDocuments({ clientId: id });
  const { data: payments } = usePayments({ thirdPartyType: 'client', thirdPartyId: id });
  const { data: companySettings } = useCompanySettings();
  const { data: clientReps } = useClientRepresentatives(id ?? '');
  const { data: allReps } = useRepresentatives();
  const linkRep = useLinkRepresentativeToClient();
  const unlinkRep = useUnlinkRepresentativeFromClient();

  const [addRepOpen, setAddRepOpen] = useState(false);
  const [repSearch, setRepSearch] = useState('');
  const [createRepOpen, setCreateRepOpen] = useState(false);

  // "Representatives" tab is the union of (A) reps explicitly linked via M:N
  // and (B) reps that appear on any sales doc for this client. Picking a rep
  // on a one-off doc without the manual M:N link should still surface here.
  const relatedReps = useMemo<Representative[]>(() => {
    const byId = new Map<string, Representative>();
    for (const r of clientReps ?? []) byId.set(r.id, r);
    if (allReps && salesDocs) {
      for (const d of salesDocs) {
        if (!d.representativeId || byId.has(d.representativeId)) continue;
        const match = allReps.find((r) => r.id === d.representativeId);
        if (match) byId.set(match.id, match);
      }
    }
    return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [clientReps, allReps, salesDocs]);

  const linkedRepIds = useMemo(
    () => new Set((clientReps ?? []).map((r) => r.id)),
    [clientReps],
  );

  const stats = useMemo(() => {
    const docs = salesDocs ?? [];
    const counts = {
      quote: 0, order: 0, delivery: 0, invoice: 0, credit_note: 0,
    } as Record<string, number>;
    for (const d of docs) counts[d.docType] = (counts[d.docType] ?? 0) + 1;
    const totalInvoiced = docs.filter((d) => d.docType === 'invoice').reduce((s, d) => s + d.totalTtc, 0);
    const totalCredited = docs.filter((d) => d.docType === 'credit_note').reduce((s, d) => s + d.totalTtc, 0);
    const totalPaid = (payments ?? []).reduce((s, p) => s + p.amount, 0);
    return {
      counts,
      totalInvoiced, totalCredited, totalPaid,
      balance: totalInvoiced - totalCredited - totalPaid,
    };
  }, [salesDocs, payments]);

  const filteredDocs = useMemo(() => {
    if (!salesDocs) return [];
    if (docFilter === 'all') return salesDocs;
    return salesDocs.filter((d) => d.docType === docFilter);
  }, [salesDocs, docFilter]);
  const documentTypeFilterOptions = useMemo(
    () => DOC_TYPES_ORDER
      .filter((type) => (stats.counts[type] ?? 0) > 0)
      .map((type) => ({
        value: type,
        label: `${t(DOC_TYPE_LABELS[type]!)} (${stats.counts[type] ?? 0})`,
      })),
    [stats.counts, t],
  );

  // Loading only when we have nothing — placeholderData fills `client` from
  // the list cache, so the page renders instantly even offline.
  if (isLoading && !client) {
    return <div className="flex items-center justify-center py-12 text-muted-foreground">{t('common.loading')}</div>;
  }
  if (!client) {
    return <div className="flex items-center justify-center py-12 text-muted-foreground">{t('common.noResults')}</div>;
  }

  return (
    <div className="space-y-4">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 -mx-4 px-4 py-3 bg-background/95 backdrop-blur border-b">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-bold truncate">{client.name}</h2>
              <Badge variant="outline">{client.type === 'company' ? t('clients.typeCompany') : t('clients.typeIndividual')}</Badge>
              {client.isDefault && <Badge>{t('stock.default')}</Badge>}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              {client.code}
              {client.city && <> · {client.city}</>}
              {client.phone && <> · {client.phone}</>}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <Pencil size={14} className="me-1.5" />
              {t('common.edit')}
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/clients')}>
              {t('common.back')}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
          <Kpi label={t('clients.profile.totalInvoiced')} value={`${formatPrice(stats.totalInvoiced)} DT`} />
          <Kpi label={t('clients.profile.totalPaid')} value={`${formatPrice(stats.totalPaid)} DT`} />
          <Kpi label={t('clients.profile.totalCredited')} value={`${formatPrice(stats.totalCredited)} DT`} />
          <Kpi label={t('clients.profile.balance')} value={`${formatPrice(stats.balance)} DT`} positive={stats.balance <= 0} />
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">{t('clients.profile.tabs.overview')}</TabsTrigger>
          <TabsTrigger value="documents">{t('clients.profile.tabs.documents')} ({salesDocs?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="representatives">
            {t('representatives.title')} ({relatedReps.length})
          </TabsTrigger>
          <TabsTrigger value="payments">{t('clients.profile.tabs.payments')}</TabsTrigger>
        </TabsList>

        {/* Tab 1 — Overview */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardContent className="pt-5 space-y-3">
                <h3 className="text-sm font-semibold">{t('clients.profile.info')}</h3>
                <div className="space-y-2 text-sm">
                  {client.taxId && <InfoRow label={t('clients.taxId')} value={client.taxId} />}
                  {client.address && <InfoRow label={t('clients.address')} value={client.address} />}
                  {client.city && <InfoRow label={t('clients.city')} value={client.city} />}
                  {client.phone && <InfoRow label={t('clients.phone')} value={client.phone} />}
                  {client.email && <InfoRow label={t('clients.email')} value={client.email} />}
                  {companySettings?.showClientCarNumber !== false && client.carRegistrationNumber && (
                    <InfoRow label={t('clients.carRegistrationNumber')} value={client.carRegistrationNumber} />
                  )}
                  <Separator />
                  <InfoRow label={t('clients.defaultDiscount')} value={`${client.defaultDiscountPct}%`} />
                  <InfoRow label={t('clients.paymentTerms')} value={`${client.paymentTermsDays} ${t('common.date').toLowerCase()}`} />
                  {client.notes && (
                    <>
                      <Separator />
                      <p className="text-muted-foreground whitespace-pre-line">{client.notes}</p>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-5 space-y-3">
                <h3 className="text-sm font-semibold">{t('clients.profile.documentSummary')}</h3>
                <div className="space-y-2 text-sm">
                  <CountRow label={t('sales.quotes')} count={stats.counts.quote ?? 0} />
                  <CountRow label={t('sales.orders')} count={stats.counts.order ?? 0} />
                  <CountRow label={t('sales.deliveries')} count={stats.counts.delivery ?? 0} />
                  <CountRow label={t('sales.invoices')} count={stats.counts.invoice ?? 0} />
                  <CountRow label={t('sales.creditNotes')} count={stats.counts.credit_note ?? 0} />
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab 2 — Documents */}
        <TabsContent value="documents">
          <Card>
            <CardContent className="pt-5 space-y-3">
              {(documentTypeFilterOptions.length > 1 || docFilter !== 'all') && (
                <FilterBar className="mb-0 w-full">
                  <FilterField label={t('dashboard.type')} minWidth={220}>
                    <SearchableSelect
                      options={documentTypeFilterOptions}
                      value={docFilter === 'all' ? '' : docFilter}
                      onChange={(value) => setDocFilter(value || 'all')}
                      label={t('dashboard.type')}
                      placeholder={`${t('common.all')} (${salesDocs?.length ?? 0})`}
                      searchPlaceholder={t('common.search')}
                      className="h-8"
                    />
                  </FilterField>
                </FilterBar>
              )}
              <div className="rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('dashboard.type')}</TableHead>
                      <TableHead>{t('sales.number')}</TableHead>
                      <TableHead>{t('sales.docDate')}</TableHead>
                      <TableHead className="text-end">{t('sales.totalTtc')}</TableHead>
                      <TableHead className="w-8" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!filteredDocs.length ? (
                      <TableRow><TableCell colSpan={5} className="text-center py-6 text-muted-foreground">{t('common.noResults')}</TableCell></TableRow>
                    ) : filteredDocs.map((doc) => (
                      <TableRow key={doc.id}>
                        <TableCell><Badge variant="outline">{t(DOC_TYPE_LABELS[doc.docType] ?? doc.docType)}</Badge></TableCell>
                        <TableCell className="font-medium">{doc.number}</TableCell>
                        <TableCell>{formatDate(doc.docDate)}</TableCell>
                        <TableCell className="text-end">{formatPrice(doc.totalTtc)} DT</TableCell>
                        <TableCell>
                          <Link to={`${DOC_PATHS[doc.docType]}/${doc.id}`}>
                            <Button variant="ghost" size="icon-xs"><ArrowRight size={14} className="rtl:rotate-180" /></Button>
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab — Representatives (M:N link) */}
        <TabsContent value="representatives">
          <Card>
            <CardContent className="pt-5 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold">{t('representatives.title')}</h3>
                <Button size="sm" onClick={() => setAddRepOpen(true)}>
                  <Plus data-icon="inline-start" size={14} />
                  {t('representatives.addToClient')}
                </Button>
              </div>
              <div className="rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('representatives.name')}</TableHead>
                      <TableHead>{t('representatives.phone')}</TableHead>
                      <TableHead>{t('representatives.email')}</TableHead>
                      <TableHead className="w-8" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {!relatedReps.length ? (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-6 text-muted-foreground">
                          {t('representatives.noneLinked')}
                        </TableCell>
                      </TableRow>
                    ) : relatedReps.map((r) => {
                      const isLinked = linkedRepIds.has(r.id);
                      return (
                        <TableRow key={r.id}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <Link to={`/representatives/${r.id}`} className="hover:underline">
                                {r.name}
                              </Link>
                              {!isLinked && (
                                <Badge variant="outline" className="text-[9px] font-normal text-muted-foreground">
                                  {t('representatives.viaDocument')}
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{r.phone ?? <span className="text-muted-foreground">—</span>}</TableCell>
                          <TableCell>{r.email ?? <span className="text-muted-foreground">—</span>}</TableCell>
                          <TableCell>
                            {isLinked ? (
                              <Button
                                variant="ghost"
                                size="icon-xs"
                                onClick={() => unlinkRep.mutate({ clientId: client.id, representativeId: r.id })}
                                aria-label={t('representatives.unlink')}
                                title={t('representatives.unlink')}
                              >
                                <Trash2 size={14} className="text-muted-foreground" />
                              </Button>
                            ) : (
                              <Button
                                variant="ghost"
                                size="xs"
                                className="text-xs h-7"
                                onClick={() => linkRep.mutate({ clientId: client.id, representativeId: r.id })}
                                title={t('representatives.link')}
                              >
                                {t('representatives.link')}
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Link representative picker */}
          <Dialog
            open={addRepOpen}
            onOpenChange={(v) => { setAddRepOpen(v); if (!v) setRepSearch(''); }}
          >
            <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col gap-4">
              <DialogHeader>
                <DialogTitle>{t('representatives.addToClient')}</DialogTitle>
              </DialogHeader>
              <div className="flex gap-2 items-center">
                <div className="relative flex-1">
                  <Search
                    size={14}
                    className="absolute start-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none"
                  />
                  <Input
                    value={repSearch}
                    onChange={(e) => setRepSearch(e.target.value)}
                    placeholder={t('common.search')}
                    className="ps-8 h-9"
                    autoFocus
                  />
                </div>
                <Button size="sm" variant="outline" onClick={() => setCreateRepOpen(true)}>
                  <Plus size={14} className="me-1.5" />
                  {t('representatives.newRepresentative')}
                </Button>
              </div>
              <div className="flex-1 overflow-y-auto border rounded-lg min-h-[260px]">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead>{t('representatives.name')}</TableHead>
                      <TableHead>{t('representatives.phone')}</TableHead>
                      <TableHead>{t('representatives.email')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(() => {
                      const linked = new Set((clientReps ?? []).map((r) => r.id));
                      const filtered = (allReps ?? []).filter((r) => {
                        if (linked.has(r.id)) return false;
                        if (!repSearch) return true;
                        const q = repSearch.toLowerCase();
                        return (
                          r.name.toLowerCase().includes(q)
                          || (r.phone ?? '').toLowerCase().includes(q)
                          || (r.email ?? '').toLowerCase().includes(q)
                        );
                      });
                      if (!filtered.length) {
                        return (
                          <TableRow>
                            <TableCell colSpan={3} className="text-center py-10 text-muted-foreground">
                              {t('common.noResults')}
                            </TableCell>
                          </TableRow>
                        );
                      }
                      return filtered.map((r) => (
                        <TableRow
                          key={r.id}
                          className="cursor-pointer hover:bg-accent"
                          onClick={async () => {
                            await linkRep.mutateAsync({ clientId: client.id, representativeId: r.id });
                            setAddRepOpen(false);
                            setRepSearch('');
                          }}
                        >
                          <TableCell className="font-medium">{r.name}</TableCell>
                          <TableCell>{r.phone ?? <span className="text-muted-foreground">—</span>}</TableCell>
                          <TableCell>{r.email ?? <span className="text-muted-foreground">—</span>}</TableCell>
                        </TableRow>
                      ));
                    })()}
                  </TableBody>
                </Table>
              </div>
            </DialogContent>
          </Dialog>

          <RepresentativeFormDialog
            open={createRepOpen}
            onOpenChange={setCreateRepOpen}
            onCreated={async (created) => {
              setCreateRepOpen(false);
              await linkRep.mutateAsync({ clientId: client.id, representativeId: created.id });
              setAddRepOpen(false);
              setRepSearch('');
            }}
          />
        </TabsContent>

        {/* Tab 3 — Payments & balance */}
        <TabsContent value="payments">
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Kpi label={t('clients.profile.totalInvoiced')} value={`${formatPrice(stats.totalInvoiced)} DT`} />
              <Kpi label={t('clients.profile.totalPaid')} value={`${formatPrice(stats.totalPaid)} DT`} />
              <Kpi label={t('clients.profile.balance')} value={`${formatPrice(stats.balance)} DT`} positive={stats.balance <= 0} />
            </div>
            <Card>
              <CardContent className="pt-5">
                <div className="rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('payments.number')}</TableHead>
                        <TableHead>{t('payments.method')}</TableHead>
                        <TableHead>{t('payments.date')}</TableHead>
                        <TableHead className="text-end">{t('payments.amount')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {!payments?.length ? (
                        <TableRow><TableCell colSpan={4} className="text-center py-6 text-muted-foreground">{t('common.noResults')}</TableCell></TableRow>
                      ) : payments.map((p) => (
                        <TableRow key={p.id}>
                          <TableCell>{p.number}</TableCell>
                          <TableCell>{t(`payments.${p.paymentMethod}`)}</TableCell>
                          <TableCell>{formatDate(p.paymentDate)}</TableCell>
                          <TableCell className="text-end">{formatPrice(p.amount)} DT</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      <ClientFormDialog open={editOpen} onOpenChange={setEditOpen} client={client} />
    </div>
  );
}

function Kpi({ label, value, positive }: { label: string; value: string; positive?: boolean }) {
  const tone = positive === false ? 'text-destructive' : positive === true ? 'text-green-600' : '';
  return (
    <div className="rounded-md border bg-card px-3 py-2">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`text-base font-bold leading-tight mt-0.5 ${tone}`}>{value}</p>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-end">{value}</span>
    </div>
  );
}

function CountRow({ label, count }: { label: string; count: number }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{count}</span>
    </div>
  );
}
